import type { attivita as it } from "../it/attivita.ts";

type P = { attore: string; stack: string; revisione?: number };

/** Nelle frasi dello stack, `stack` è un segnaposto che l'interfaccia sostituisce con un link. */
export const attivita: typeof it = {
  "attivita.titolo": "Activity",
  "attivita.intro": "Who did what on Portman, webhooks included. Entries are append-only: they can't be edited.",
  "attivita.vuoto": "No activity",
  "attivita.intervallo": (p: { da: number; a: number; totale: number }) => `${p.da}–${p.a} of ${p.totale}`,
  "attivita.precedenti": "Previous",
  "attivita.successive": "Next",
  "attivita.tutteAzioni": "All actions",
  "attivita.tuttiStack": "All stacks",
  "attivita.tuttiUtenti": "All users",
  "attivita.filtroAzione": "Filter by action",
  "attivita.filtroStack": "Filter by stack",
  "attivita.filtroUtente": "Filter by user",
  "attivita.dettagli": "details",
  "attivita.ip": "Source IP address",
  "attivita.fallita": "failed",
  "attivita.auth.login": (p: P) => `${p.attore} signed in`,
  "attivita.auth.logout": (p: P) => `${p.attore} signed out`,
  "attivita.auth.password_change": (p: P) => `${p.attore} changed their password`,
  "attivita.backup.export": (p: P) => `${p.attore} downloaded a database backup`,
  "attivita.backup.restore": (p: P) => `${p.attore} restored the database from a backup`,
  "attivita.cartella.create": (p: P) => `${p.attore} added a host folder`,
  "attivita.cartella.delete": (p: P) => `${p.attore} removed a host folder`,
  "attivita.notifiche.save": (p: P) => `${p.attore} changed email and alert settings`,
  "attivita.notifiche.test": (p: P) => `${p.attore} sent a test email`,
  "attivita.registry.save": (p: P) => `${p.attore} saved a private registry`,
  "attivita.registry.remove": (p: P) => `${p.attore} removed a private registry`,
  "attivita.stack.create": (p: P) => `${p.attore} created ${p.stack}`,
  "attivita.stack.duplicate": (p: P) => `${p.attore} duplicated ${p.stack}`,
  "attivita.stack.save": (p: P) => `${p.attore} saved a new revision of ${p.stack}`,
  "attivita.stack.update": (p: P) => `${p.attore} edited ${p.stack}`,
  "attivita.stack.rename": (p: P) => `${p.attore} renamed ${p.stack}`,
  "attivita.stack.delete": (p: P) => `${p.attore} deleted ${p.stack}`,
  "attivita.stack.import": (p: P) => `${p.attore} imported ${p.stack}`,
  "attivita.stack.avvisi.save": (p: P) => `${p.attore} changed the alerts of ${p.stack}`,
  "attivita.stack.deploy": (p: P) =>
    `${p.attore} deployed ${p.stack}${p.revisione === undefined ? "" : ` rev. ${p.revisione}`}`,
  "attivita.stack.stop": (p: P) => `${p.attore} stopped ${p.stack}`,
  "attivita.stack.restart": (p: P) => `${p.attore} restarted ${p.stack}`,
  "attivita.token.create": (p: P) => `${p.attore} created a read token`,
  "attivita.token.revoke": (p: P) => `${p.attore} revoked a read token`,
  "attivita.user.bootstrap": (p: P) => `${p.attore} created the first account`,
  "attivita.user.create": (p: P) => `${p.attore} created a user`,
  "attivita.user.update": (p: P) => `${p.attore} edited a user`,
  "attivita.user.delete": (p: P) => `${p.attore} deleted a user`,
  "attivita.webhook.create": (p: P) => `${p.attore} created a webhook`,
  "attivita.webhook.revoke": (p: P) => `${p.attore} revoked a webhook`,
  "attivita.impostazioni.lingua": (p: P) => `${p.attore} changed the instance language`,
  "attivita.filtro.auth.login": "Sign-ins",
  "attivita.filtro.auth.logout": "Sign-outs",
  "attivita.filtro.auth.password_change": "Password changes",
  "attivita.filtro.backup.export": "Backups downloaded",
  "attivita.filtro.backup.restore": "Restores",
  "attivita.filtro.cartella.create": "Folders added",
  "attivita.filtro.cartella.delete": "Folders removed",
  "attivita.filtro.notifiche.save": "Email and alerts",
  "attivita.filtro.notifiche.test": "Test emails",
  "attivita.filtro.registry.save": "Registries saved",
  "attivita.filtro.registry.remove": "Registries removed",
  "attivita.filtro.stack.create": "Stacks created",
  "attivita.filtro.stack.duplicate": "Stacks duplicated",
  "attivita.filtro.stack.save": "Revisions saved",
  "attivita.filtro.stack.update": "Stacks edited",
  "attivita.filtro.stack.rename": "Stacks renamed",
  "attivita.filtro.stack.delete": "Stacks deleted",
  "attivita.filtro.stack.import": "Stacks imported",
  "attivita.filtro.stack.avvisi.save": "Alerts changed",
  "attivita.filtro.stack.deploy": "Deploys",
  "attivita.filtro.stack.stop": "Stops",
  "attivita.filtro.stack.restart": "Restarts",
  "attivita.filtro.token.create": "Tokens created",
  "attivita.filtro.token.revoke": "Tokens revoked",
  "attivita.filtro.user.bootstrap": "First account",
  "attivita.filtro.user.create": "Users created",
  "attivita.filtro.user.update": "Users edited",
  "attivita.filtro.user.delete": "Users deleted",
  "attivita.filtro.webhook.create": "Webhooks created",
  "attivita.filtro.webhook.revoke": "Webhooks revoked",
  "attivita.filtro.impostazioni.lingua": "Language changes",
  "attivita.ip.block": (p: P) => `${p.attore} blocked an IP address`,
  "attivita.ip.unblock": (p: P) => `${p.attore} unblocked an IP address`,
  "attivita.filtro.ip.block": "IPs blocked",
  "attivita.filtro.ip.unblock": "IPs unblocked",
};
